/**
 * PackReadinessStrip.jsx — RC 1.48.4B3 (2026-05-04)
 *
 * Recruiter-grade readiness checklist for the Application Pack
 * Detail page. Renders four chips that mirror the trust posture of
 * the exported premium PDF:
 *
 *   1. Tailored Variant     — present (✓) or missing (✗)
 *   2. Cover Letter         — present (✓) or absent (—, neutral)
 *   3. Canonical Fit        — present (✓), stale (⏱), or missing (✗)
 *   4. Governance Verdict   — pursue / tailor / reposition / develop
 *                             / manual_review / monitor (or no_run)
 *
 * Recruiter email status is intentionally NOT surfaced here
 * (deferred per operator direction in RC 1.48.4B3).
 *
 * Pure read-only — sole input is the `pack` prop. No fetches,
 * no mutations.
 */

import { CheckCircle2, Circle, AlertTriangle, Clock, FileText, Mail, Gauge, Scale } from 'lucide-react';

const VERDICT_TINT = {
  pursue:        'bg-ndor-success-light text-ndor-success border-ndor-success/20',
  tailor:        'bg-ndor-info-light text-ndor-info border-ndor-info/20',
  reposition:    'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20',
  develop:       'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20',
  manual_review: 'bg-ndor-danger-light text-ndor-danger border-ndor-danger/20',
  monitor:       'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge',
  no_run:        'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge',
};

function _verdictKeyword(label) {
  if (!label || typeof label !== 'string') return 'no_run';
  const head = label.split(/[/·\s]/)[0].trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(VERDICT_TINT, head) ? head : 'no_run';
}

function Chip({ icon: Icon, label, value, tone, testid }) {
  const toneClass = {
    ok:      'bg-ndor-success-light text-ndor-success border-ndor-success/20',
    neutral: 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge',
    warn:    'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20',
    bad:     'bg-ndor-danger-light text-ndor-danger border-ndor-danger/20',
  }[tone] || 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge';
  return (
    <div
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${toneClass}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span className="text-ndor-ink-muted/90">{label}</span>
      <span className="text-ndor-ink font-medium">{value}</span>
    </div>
  );
}

export default function PackReadinessStrip({ pack }) {
  if (!pack) return null;

  const variant = pack.cv_variant;
  const letter  = pack.cover_letter;
  const cf      = pack.canonical_fit;
  const fitStatus = pack.fit_status || 'missing';

  // Variant chip — pack documents are immutable, so the cv_variant
  // summary is always carried on the pack response when bound.
  // RC 1.49.x.PACK_BINDING_DIAGNOSTIC Fix A (operator-locked,
  // 2026-05-06) — Source-CV-only packs (cv_variant_id IS NULL but
  // base_cv_id IS NOT NULL) are LEGITIMATE complete packs, not a
  // defect. Render them as a neutral "CV / Source CV" chip
  // instead of the alarming red "Not bound".
  let variantChip;
  if (variant) {
    variantChip = { icon: CheckCircle2, label: 'Variant', value: 'Tailored CV ready', tone: 'ok' };
  } else if (pack.base_cv_id) {
    variantChip = { icon: CheckCircle2, label: 'CV', value: 'Source CV', tone: 'ok' };
  } else {
    variantChip = { icon: Circle, label: 'Variant', value: 'Not bound', tone: 'bad' };
  }

  // Cover letter — absence is acceptable (some packs ship CV-only).
  const letterChip = letter
    ? { icon: CheckCircle2, label: 'Cover letter', value: 'Attached', tone: 'ok' }
    : { icon: Mail,         label: 'Cover letter', value: '—',         tone: 'neutral' };

  // Canonical fit — six-tier mapping mirrored from PackFitPanel.
  // Score normaliser — backend canonical fit can land as a 0–1 unit
  // proportion OR an already-scaled 0–100 integer depending on the
  // upstream code path.  PackFitPanel handles this asymmetry; mirror
  // the same coercion here so the chip never reads `0.72/100`.
  const _fitScore100 = (raw) => {
    if (raw == null || Number.isNaN(Number(raw))) return null;
    const n = Number(raw);
    return Math.round(n <= 1 ? n * 100 : n);
  };
  let fitChip;
  if (fitStatus === 'current' && cf) {
    const score100 = _fitScore100(cf.fit_score);
    fitChip = {
      icon: Gauge, label: 'Canonical fit',
      value: `${score100 ?? '—'}/100`, tone: 'ok',
    };
  } else if (fitStatus === 'stale_rubric' && cf) {
    const score100 = _fitScore100(cf.fit_score);
    fitChip = {
      icon: Clock, label: 'Canonical fit',
      value: `${score100 ?? '—'}/100 · rubric updated`,
      tone: 'warn',
    };
  } else if (fitStatus === 'initial_source_cv') {
    fitChip = {
      icon: AlertTriangle, label: 'Canonical fit',
      value: 'Source CV baseline only', tone: 'warn',
    };
  } else if (fitStatus === 'tombstoned_variant_body') {
    fitChip = {
      icon: AlertTriangle, label: 'Canonical fit',
      value: 'Variant body expired', tone: 'warn',
    };
  } else if (fitStatus === 'stale') {
    fitChip = {
      icon: Clock, label: 'Canonical fit',
      value: 'Stale', tone: 'warn',
    };
  } else {
    fitChip = {
      icon: Circle, label: 'Canonical fit',
      value: 'Not analysed', tone: 'bad',
    };
  }

  // Governance verdict — read from canonical_fit.final_decision when
  // present; degrade to "Not analysed" when missing.
  const verdictLabel = (cf && (cf.final_decision || cf.governance_decision)) || null;
  const verdictKw = _verdictKeyword(verdictLabel || 'no_run');
  const verdictDisplay = verdictLabel
    ? verdictLabel.replaceAll('_', ' ')
    : 'Not analysed';

  return (
    <div
      data-testid="pack-readiness-strip"
      className="flex flex-wrap items-center gap-2 rounded-xl border border-ndor-edge bg-ndor-surface px-3 py-2.5"
      role="status"
      aria-label="Pack readiness"
    >
      <span className="text-[11px] uppercase tracking-wider text-ndor-ink-muted font-medium pl-1 pr-2">
        Pack readiness
      </span>
      <Chip {...variantChip} testid="pack-readiness-variant" />
      <Chip {...letterChip}  testid="pack-readiness-cover-letter" />
      <Chip {...fitChip}     testid="pack-readiness-canonical-fit" />
      <div
        data-testid="pack-readiness-governance-verdict"
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${VERDICT_TINT[verdictKw]}`}
      >
        <Scale className="h-3.5 w-3.5" aria-hidden />
        <span className="text-ndor-ink-muted/90">Governance</span>
        <span className="font-medium uppercase tracking-wide">{verdictDisplay}</span>
      </div>
      <FileText className="hidden" aria-hidden /> {/* keep import warm for IDE */}
    </div>
  );
}
